// src/components/EditProfile.js
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import useNoIndex from "../hooks/useNoIndex";
import { reportCheckoutSuccessConversion } from "../utils/tracking";
import { standardUserApi } from "../services/standardUserApi";
import InviteModal from "./InviteModal";
import Footer from "./Footer";
import MemberFaqs from "./MemberFaqs";
import LoadingState from "./LoadingState";
import { PENDING_CONNECT_TOKEN_KEY } from "./ConnectViaLink";

// Initialize Memberstack using your public key
const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({ publicKey: currentKey });

const EditProfile = () => {
  const navigate = useNavigate();

  // Prevent search engines from indexing this member-only page
  useNoIndex();

  const [hasValidSubscriptionStatus, setHasValidSubscriptionStatus] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(true);
  const [firstName, setFirstName] = useState("");
  // Invite Links: only shown to a currently-active (not trialing) paying
  // subscriber - stricter than hasValidSubscriptionStatus above, which
  // also allows trialing/cancelled since that's enough to view this page.
  // Unlike My Gatherings/My Events, this page has no theme of its own, so
  // the subscriber picks one from a dropdown before generating a link.
  const [canInvite, setCanInvite] = useState(false);
  const [themes, setThemes] = useState([]);
  const [selectedInviteThemeId, setSelectedInviteThemeId] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [connectSuccessMessage, setConnectSuccessMessage] = useState("");

  // Handle post-checkout purchase tracking + cleanup. Checkout.js now
  // redirects to /my-events on success, not here - this stays as a
  // harmless safety net (idempotent, sessionStorage-gated) for anyone who
  // still lands here shortly after checkout some other way.
  useEffect(() => {
    const handlePurchaseSuccess = () => reportCheckoutSuccessConversion('/edit-profile');

    handlePurchaseSuccess();
  }, []);

  useEffect(() => {
    // A memberstackToken means this browser already completed a real
    // login/signup, so a single transient failure (network blip, SDK not
    // fully settled right after a redirect) shouldn't immediately be
    // treated as "logged out" - retry once before giving up.
    const getCurrentMemberWithRetry = async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const currentMember = await memberstack.getCurrentMember();
          if (currentMember && currentMember.data) {
            return currentMember;
          }
        } catch (err) {
          console.error("EditProfile › getCurrentMember error:", err);
        }
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 800));
        }
      }
      return null;
    };

    const validateAndCheck = async () => {
      const token = localStorage.getItem("memberstackToken");

      if (!token) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      const currentMember = await getCurrentMemberWithRetry();

      if (!currentMember) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      setIsVerifyingAuth(false);
      setFirstName(currentMember.data.customFields?.["first-name"] || currentMember.data.auth?.email?.split("@")[0] || "");

      let hasValidStatus = false;
      for (const plan of currentMember.data.planConnections) {
        const status = plan.status?.toLowerCase();
        const type = plan.type?.toLowerCase();

        if (type === "subscription" &&
          (status === "active" || status === "trialing" || status === "cancelled" || status === "canceled")) {
          hasValidStatus = true;
          break;
        }
      }
      setHasValidSubscriptionStatus(hasValidStatus);

      if (!hasValidStatus) {
        navigate("/checkout");
        return;
      }

      // If this member just signed up or logged in from someone's personal
      // "connect with me" link (see ConnectViaLink.js), finish that request
      // now that they're a confirmed subscriber.
      const pendingConnectToken = localStorage.getItem(PENDING_CONNECT_TOKEN_KEY);
      if (pendingConnectToken) {
        localStorage.removeItem(PENDING_CONNECT_TOKEN_KEY);
        try {
          const result = await standardUserApi.acceptConnectionViaLink(token, pendingConnectToken);
          setConnectSuccessMessage(`Connection request sent to ${result.otherUser.firstName}!`);
        } catch (err) {
          console.error("EditProfile › resume pending connect error:", err);
        }
      }

      const isActivePaying = currentMember.data.planConnections.some((plan) => {
        const status = plan.status?.toLowerCase();
        const type = plan.type?.toLowerCase();
        return type === "subscription" && status === "active";
      });
      setCanInvite(isActivePaying);
      if (isActivePaying) {
        try {
          const lookupData = await standardUserApi.getLookupData(token);
          const publishedThemes = (lookupData.themes || []).filter((t) => t.landing_page_slug);
          setThemes(publishedThemes);
          if (publishedThemes.length > 0) {
            setSelectedInviteThemeId(publishedThemes[0].id);
          }
        } catch (err) {
          console.error("Failed to load themes for Invite:", err);
        }
      }
    };

    validateAndCheck();
  }, []);

  // When user clicks "Manage Subscription", launch the Stripe customer portal.
  const handleManageSubscription = async () => {
    try {
      await memberstack.launchStripeCustomerPortal();
    } catch (error) {
      alert("Failed to launch subscription portal. Please try again.");
    }
  };

  if (isVerifyingAuth) {
    return <LoadingState />;
  }

  return (
    <div className="page-wrapper" style={{ position: "relative" }}>
      {/* Login Modal */}
      {showLoginModal && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0,
            width: "100%", height: "100%",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: "80px",
            zIndex: 1200
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "32px",
              borderRadius: "12px",
              textAlign: "center",
              maxWidth: "400px",
              width: "80%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
          >
            <h3 style={{
              marginBottom: "16px",
              color: "#1F2937",
              fontSize: "1.25rem",
              fontWeight: "700"
            }}>
              Session Expired
            </h3>
            <p style={{
              marginBottom: "24px",
              color: "#4B5563",
              lineHeight: "1.5"
            }}>
              Your session has expired. Please log in again to continue.
            </p>
            <button
              className="button-2 is-black w-button"
              onClick={() => navigate("/login")}
              style={{
                width: "100%",
                marginBottom: "16px",
              }}
            >
              Log In
            </button>
            {/* Only offer signup to visitors who never had a session - a
                token here means this is an existing member whose live
                session check failed, and sending them to create an account
                would collide with their already-registered email. */}
            {!localStorage.getItem("memberstackToken") && (
              <p style={{
                margin: 0,
                color: "#6B7280",
                fontSize: "0.9rem"
              }}>
                New here?{" "}
                <button
                  onClick={() => navigate("/gathering-matcher")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#8B5FBF",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    padding: 0,
                  }}
                >
                  Create an account
                </button>
              </p>
            )}
          </div>
        </div>
      )}

      <main className="main-wrapper">
        <section className="section_hero">
          <div className="padding-global padding-section-large container-large">
            <div className="hero_card is-vertical">
              <div style={{ maxWidth: "480px", margin: "0 auto" }}>
                {connectSuccessMessage && (
                  <div style={{
                    background: "#f0fdf4",
                    border: "1px solid rgba(16, 185, 129, 0.3)",
                    borderRadius: "10px",
                    padding: "12px 16px",
                    marginBottom: "20px",
                    color: "#15803D",
                    fontWeight: "600",
                    fontSize: "0.9rem",
                    textAlign: "center",
                  }}>
                    {connectSuccessMessage} 🎉
                  </div>
                )}
                <h4 style={{ textAlign: "center", marginBottom: "24px", fontSize: "1.25rem" }}>OkGather Profile</h4>
                <div style={{
                  background: "#f0fdf4",
                  border: "1px solid rgba(16, 185, 129, 0.25)",
                  borderRadius: "12px",
                  padding: "24px 28px",
                  marginBottom: "32px",
                }}>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0, fontSize: "1.2rem", color: "#333", lineHeight: "2.6" }}>
                    <li>✅ &nbsp;Attend <strong>as many events as you like</strong></li>
                    <li>🍹 &nbsp;<strong>20% off food and drinks</strong> at the venue. Show this page at the bar</li>
                    <li>🤝 &nbsp;Access to <strong>members-only</strong> gatherings</li>
                  </ul>
                </div>

                <button
                  onClick={() => navigate("/events")}
                  className="button-2 is-black w-button"
                  style={{ width: "100%", marginBottom: "16px" }}
                >
                  Browse Gatherings
                </button>

                {canInvite && themes.length > 0 && (
                  <div style={{
                    background: "linear-gradient(135deg, #25D366 0%, #128C4A 100%)",
                    borderRadius: "16px",
                    padding: "24px 28px",
                    marginBottom: "24px",
                    textAlign: "center",
                    boxShadow: "0 8px 24px rgba(18, 140, 74, 0.35)",
                  }}>
                    <h4 style={{ color: "#fff", margin: "0 0 6px", fontSize: "1.25rem", fontWeight: "800" }}>
                      🎉 Invite someone to join
                    </h4>
                    <p style={{ color: "rgba(255,255,255,0.9)", margin: "0 0 16px", fontSize: "0.95rem", lineHeight: "1.5" }}>
                      Know someone who'd love OkGather? Invite them to sign up with their own account in seconds.
                    </p>
                    <select
                      value={selectedInviteThemeId || ""}
                      onChange={(e) => setSelectedInviteThemeId(Number(e.target.value))}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "none",
                        fontSize: "0.95rem",
                        marginBottom: "12px",
                      }}
                    >
                      {themes.map((theme) => (
                        <option key={theme.id} value={theme.id}>{theme.theme_name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowInviteModal(true)}
                      style={{
                        width: "100%",
                        padding: "13px",
                        borderRadius: "10px",
                        border: "none",
                        backgroundColor: "#fff",
                        color: "#128C4A",
                        fontWeight: "700",
                        fontSize: "1.02rem",
                        cursor: "pointer",
                        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
                        transition: "transform 0.15s ease",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.03)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                    >
                      🔗 Get Invite Link
                    </button>
                  </div>
                )}

                <Link
                  to="/my-events"
                  style={{
                    display: "block",
                    boxSizing: "border-box",
                    textAlign: "center",
                    width: "100%",
                    padding: "12px 32px",
                    borderRadius: "50px",
                    border: "2px solid #8B5FBF",
                    color: "#8B5FBF",
                    fontWeight: "700",
                    fontSize: "1rem",
                    textDecoration: "none",
                    marginBottom: "32px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#8B5FBF";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#8B5FBF";
                  }}
                >
                  View My Events
                </Link>

                <MemberFaqs>
                  {hasValidSubscriptionStatus && (
                    <div style={{
                      background: "#fafafa",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      padding: "20px 24px",
                      textAlign: "center",
                    }}>
                      <button
                        style={{
                          padding: "10px 20px",
                          backgroundColor: "#6B7280",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.95rem",
                        }}
                        onClick={handleManageSubscription}
                      >
                        Manage Account
                      </button>
                    </div>
                  )}
                </MemberFaqs>

              </div>
            </div>
          </div>
        </section>
        <Footer />
      </main>

      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        themeId={selectedInviteThemeId}
        themeName={themes.find((t) => t.id === selectedInviteThemeId)?.theme_name || "OkGather"}
        gatheringId={null}
        gatheringDateLabel={null}
      />
    </div>
  );
};

export default EditProfile;